import {Directive, EmbeddedViewRef, Input, TemplateRef, ViewContainerRef, ɵstringify as stringify} from '@angular/core';
import {PermissionService} from '@services/permission.service';
import {NgIfContext} from '@angular/common';

/**
 * SECURE DIRECTIVE
 *
 * Used to compile or not an element of the DOM depending on a user permission.
 *
 * EXAMPLE OF USE
 *
 * Simple secure directive: <span *siSecure="['ROLE_GET_USERS']"></span>
 * Multiple secure directive: <span *siSecure="['ROLE_GET_USERS', 'ROLE_UPDATE_USERS']"></span>
 * Multiple secure directive with logical operation:<span *siSecure="['ROLE_GET_USERS', 'ROLE_UPDATE_USERS']; operation:'or'"></span>
 */

@Directive({
  selector: '[siSecure]'
})
export class SecureDirective {
  // Operation to apply in the permissions. How to use: *siSecure="['permission1', 'permission2']; operation: 'or'"
  private _context: NgIfContext<boolean> = new NgIfContext<boolean>();
  private _thenTemplateRef: TemplateRef<NgIfContext<boolean>> | null = null;
  private _elseTemplateRef: TemplateRef<NgIfContext<boolean>> | null = null;
  private _thenViewRef: EmbeddedViewRef<NgIfContext<boolean>> | null = null;
  private _elseViewRef: EmbeddedViewRef<NgIfContext<boolean>> | null = null;
  private _permissions: string[];
  private _operation: string;

  constructor(
    private permissionService: PermissionService,
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef
  ) {
    this._thenTemplateRef = templateRef;
  }

  /**
   * The permissions to evaluate as the condition for showing a template.
   * permissions Array of all permission names we want to check to create the element that has the secure directive.
   */
  @Input()
  set siSecure(permissions: string[]) {
    this._permissions = permissions;
    this._context.ngIf = this.checkPermission();
    this._context.$implicit = this._context.ngIf;
    this._updateView();
  }

  /**
   * Operation to apply in the permissions.
   */
  @Input()
  set siSecureOperation(operation: string) {
    this._operation = operation;
    this._context.ngIf = this.checkPermission();
    this._context.$implicit = this._context.ngIf;
    this._updateView();
  }

  /**
   * A template to show if the user has the permissions.
   */
  @Input()
  set siSecureThen(templateRef: TemplateRef<NgIfContext<boolean>> | null) {
    this.assertTemplate('siSecureThen', templateRef);
    this._thenTemplateRef = templateRef;
    this._thenViewRef = null;  // clear previous view if any.
    this._updateView();
  }

  /**
   * A template to show if the user does not have the permissions.
   */
  @Input()
  set siSecureElse(templateRef: TemplateRef<NgIfContext<boolean>> | null) {
    this.assertTemplate('siSecureElse', templateRef);
    this._elseTemplateRef = templateRef;
    this._elseViewRef = null;  // clear previous view if any.
    this._updateView();
  }

  assertTemplate(property: string, templateRef: TemplateRef<any> | null): void {
    const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
    if (!isTemplateRefOrNull) {
      throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
    }
  }

  private _updateView(): void {
    if (this._context.$implicit) {
      if (!this._thenViewRef) {
        this.viewContainer.clear();
        this._elseViewRef = null;
        if (this._thenTemplateRef) {
          this._thenViewRef =
            this.viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
        }
      }
    } else {
      if (!this._elseViewRef) {
        this.viewContainer.clear();
        this._thenViewRef = null;
        if (this._elseTemplateRef) {
          this._elseViewRef =
            this.viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
        }
      }
    }
  }

  checkPermission(): boolean {
    return this.permissionService.hasPermissions(this._permissions, this._operation);
  }
}
